/** @jsx jsx */
/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useState } from 'react';

import { useApolloClient } from '@apollo/client';
import { Switch } from '@coursera-web/cds-labs';

import { breakpoints } from '@coursera/cds-common';
import { Avatar, Button, Divider, InlineNotification, Typography2, VisuallyHidden } from '@coursera/cds-core';
import { useTracker, useVisibilityTracker } from '@coursera/event-pulse/react';

import SettingsActionCreators from 'bundles/account-settings/actions/SettingsActionCreators';
import { Sections, UpdateStates } from 'bundles/account-settings/constants/SettingsConstants';
import type { PartnerEmailPreference, UpdateStatus } from 'bundles/account-settings/types/EmailPreferences';
import { getStatusMessages } from 'bundles/user-account/utils/errorMessageUtils';

import _t from 'i18n!nls/account-settings';

type Props = {
  partnerPreferences: PartnerEmailPreference[];
  updateStatus: UpdateStatus;
  disabled: boolean;
};

const styles = {
  list: css`
    display: flex;
    flex-direction: column;
    gap: var(--cds-spacing-200);
  `,
  row: css`
    display: flex;
    align-items: center;
    gap: var(--cds-spacing-150);
  `,
  textColumn: css`
    flex: 1;
    min-width: 0;
  `,
  switchWrapper: css`
    flex-shrink: 0;
    margin-inline-start: auto;
  `,
  buttonRow: css`
    margin-block-start: var(--cds-spacing-200);
  `,
  saveButton: css`
    ${breakpoints.down('xs')} {
      width: 100%;
      max-width: unset;
    }
  `,
  inlineNotification: css`
    margin-block-start: var(--cds-spacing-200);
  `,
};

const PartnerRow = ({
  partner,
  disabled,
  onToggle,
}: {
  partner: PartnerEmailPreference;
  disabled: boolean;
  onToggle: (checked: boolean) => void;
}) => {
  const ref: React.MutableRefObject<HTMLDivElement | null> = useVisibilityTracker('view_checkbox', {
    checkbox: {
      name: 'coursera_partners_communication_email_preference',
      label: partner.name,
      checked: !!partner.value.subscriptions,
    },
  });

  return (
    <div css={styles.row} ref={ref}>
      {partner.logo && <Avatar variant="organization" size={32} imgProps={{ src: partner.logo, alt: '' }} />}
      <div css={styles.textColumn}>
        <Typography2 component="h3" variant="subtitleMedium">
          {partner.name}
        </Typography2>
      </div>
      <div css={styles.switchWrapper}>
        <Switch
          aria-label={_t('Communication from #{partnerName}', { partnerName: partner.name })}
          selected={!disabled && !!partner.value.subscriptions}
          disabled={disabled}
          onToggle={onToggle}
        />
      </div>
    </div>
  );
};

const PartnerEmailPreferencesList = ({ partnerPreferences, updateStatus, disabled }: Props) => {
  const client = useApolloClient();
  const track = useTracker();
  const [preferences, setPreferences] = useState<PartnerEmailPreference[]>(partnerPreferences);
  const [toggledPreferencesForTracking, setToggledPreferencesForTracking] = useState<{ [key: string]: boolean }>({});

  const handlePartnerToggle = (partnerId: string, checked: boolean) => {
    const updatedPreferences = preferences.map((pref) =>
      pref.id === partnerId ? { ...pref, value: { subscriptions: checked } } : pref
    );
    setPreferences(updatedPreferences);
    setToggledPreferencesForTracking((prev) => ({ ...prev, [partnerId]: true }));
    SettingsActionCreators.updatePendingChangesStatus(Sections.PARTNER_EMAIL_PREFERENCES, true);
  };

  const handleSave = () => {
    if (preferences.length === 0) return;
    SettingsActionCreators.updateEmailPreferences(Sections.PARTNER_EMAIL_PREFERENCES, preferences, client);
    // Same as old settings page: fire tracking events after save, not on toggle
    preferences.forEach((partner) => {
      if (toggledPreferencesForTracking[partner.id]) {
        track('click_checkbox', {
          checkbox: {
            name: 'coursera_partners_communication_email_preference',
            label: partner.name,
            checked: !!partner.value.subscriptions,
          },
        });
      }
    });
    setToggledPreferencesForTracking({});
  };

  const isUpdating = updateStatus.state === UpdateStates.UPDATING;

  return (
    <div>
      <VisuallyHidden component="h3">{_t('Communication from Coursera partners')}</VisuallyHidden>
      <div css={styles.list} role="group" aria-label={_t('Communication from Coursera partners')}>
        {preferences.map((partner, index) => (
          <React.Fragment key={partner.id}>
            {index > 0 && <Divider color="light" orientation="horizontal" />}
            <PartnerRow
              partner={partner}
              disabled={disabled}
              onToggle={(checked) => handlePartnerToggle(partner.id, checked)}
            />
          </React.Fragment>
        ))}
      </div>

      <div css={styles.buttonRow}>
        <Button
          variant="secondary"
          size="small"
          loading={isUpdating}
          disabled={disabled}
          onClick={handleSave}
          css={styles.saveButton}
        >
          {_t('Save Changes')}
        </Button>
      </div>

      <div role="status" aria-atomic="true">
        {updateStatus.state === UpdateStates.SUCCEEDED && (
          <InlineNotification css={styles.inlineNotification} severity="success">
            {getStatusMessages.UPDATE_SUCCEEDED}
          </InlineNotification>
        )}
      </div>
      <div role="alert" aria-atomic="true">
        {updateStatus.state === UpdateStates.FAILED && (
          <InlineNotification css={styles.inlineNotification} severity="error">
            {getStatusMessages.UPDATE_FAILED}
          </InlineNotification>
        )}
      </div>
    </div>
  );
};

export default PartnerEmailPreferencesList;
